import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { analystApi, type AnalystSettingsView } from '../../services/api';

/**
 * Configurazione effettiva dell'Agente Analista (§13.3). Sono parametri del server
 * (variabili ANALYST_* nell'ambiente di PM2), uguali per tutti gli utenti: si mostrano
 * in sola lettura con il nome della variabile da cambiare.
 */

const ROWS: { key: keyof AnalystSettingsView; label: string; env: string; unit?: string }[] = [
  { key: 'reasoningModel', label: 'Modello di ragionamento (intake, esplorazione, stesura, verifica)', env: 'ANALYST_MODEL_REASONING' },
  { key: 'annotationModel', label: 'Modello delle annotazioni (audit, stance, narrative)', env: 'ANALYST_MODEL_ANNOTATION' },
  { key: 'embeddingModel', label: 'Modello degli embedding', env: 'ANALYST_EMBEDDING_MODEL' },
  { key: 'fullFetchCap', label: 'Righe oltre cui si scarica un campione', env: 'ANALYST_FULL_FETCH_CAP' },
  { key: 'freshnessMinutes', label: 'Riuso di una finestra aperta senza aggiornarla', env: 'ANALYST_FRESHNESS_MINUTES', unit: 'minuti' },
  { key: 'settleHours', label: 'Ore dopo cui una finestra è chiusa', env: 'ANALYST_SETTLE_HOURS', unit: 'ore' },
  { key: 'maxActiveRuns', label: 'Analisi in corso per utente', env: 'ANALYST_MAX_ACTIVE_RUNS' },
  { key: 'maxRunsPerHour', label: 'Analisi all\'ora per utente', env: 'ANALYST_MAX_RUNS_PER_HOUR' },
  { key: 'auditMinAgreementPct', label: 'Accordo minimo dell\'audit del sentiment', env: 'ANALYST_AUDIT_MIN_AGREEMENT_PCT', unit: '%' },
];

export function AnalystSettingsCard() {
  const [settings, setSettings] = useState<AnalystSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analystApi.settings().then(setSettings).catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-semibold text-gray-200">Agente Analista</h3>
      </div>
      <p className="text-xs text-gray-400">
        Parametri del server, uguali per tutti gli utenti: si cambiano nelle variabili d'ambiente indicate (ecosystem.config.cjs) e valgono dal riavvio successivo.
        Le credenziali Pulsar usate dall'analista sono quelle dell'integrazione Pulsar qui sopra.
      </p>
      {error && <p className="text-xs text-red-300">{error}</p>}
      {settings && (
        <dl className="divide-y divide-gray-800 text-xs">
          {ROWS.map((r) => (
            <div key={r.key} className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="text-gray-300">{r.label}<span className="block text-[11px] text-gray-500 font-mono">{r.env}</span></dt>
              <dd className="text-white font-medium text-right">{typeof settings[r.key] === 'number' ? Number(settings[r.key]).toLocaleString('it-IT') : settings[r.key]}{r.unit ? ` ${r.unit}` : ''}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
